import { Loader } from 'components';
import { TextField, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import { useSelectedLocation } from 'contexts';
import { useCustomerAPI } from 'hooks/use-customer-api';
import useTimeZone from 'hooks/use-timezone';
import {
    createContext,
    Dispatch,
    FC,
    FormEventHandler,
    RefObject,
    SetStateAction,
    useCallback,
    useContext,
    useRef,
    useState
} from 'react';
import { useNavigate } from 'react-router';
import { Customer, EdiCustomerLocation, EDIType } from 'types';
import { parseEmailList } from 'utils/format';
import EmailSelect from '../email-select';
import HourSelect from '../report-select';

const NOTES_MAX_LENGTH = 250;

type NewCustomerContextType = {
  customer: Partial<Customer>;
  formRef: RefObject<HTMLFormElement>;
  setCustomer: Dispatch<SetStateAction<Partial<Customer>>>;
  setRecipients: (contact?: EdiCustomerLocation, index?: number) => void;
  handleReset: FormEventHandler<HTMLFormElement>;
  handleSubmit: FormEventHandler<HTMLFormElement>;
};

const NewCustomerContext = createContext<NewCustomerContextType>({
  customer: {},
  formRef: { current: null },
  setCustomer: () => {},
  setRecipients: () => {},
  handleReset: () => {},
  handleSubmit: () => {}
});

const useNewCustomer = () => useContext(NewCustomerContext);

const Recipient: FC<{ recipient: EdiCustomerLocation; index: number }> = ({ recipient, index }) => {
  const { customer, setRecipients } = useNewCustomer();

  const handleChange = useCallback(
    (partial: Partial<EdiCustomerLocation>) => {
      setRecipients({ ...recipient, ...partial }, index);
    },
    [index, recipient, setRecipients]
  );

  const handleDelete = useCallback(() => {
    setRecipients(undefined, index);
  }, [index, setRecipients]);

  return (
    <>
      <div className="grid-4 p-2 mb-4 mt-4">
        <div>
          <h6 className="text-info-dark">EDI Type</h6>
          <div className="mt-3 w-100">
            <select
              className="w-100"
              onChange={e => handleChange({ ediType: e.target.value as 'GATE' | 'LIFT' | 'STATUS' })}
              value={`${recipient.ediType ?? ''}`}
              required
            >
              <option key="" value="" hidden />
              {Object.values(EDIType).map(text => (
                <option key={text} value={text}>
                  {text}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <h6 className="text-info-dark">Mapping ID</h6>
          <div className="mt-2 w-100">
            <input
              type="text"
              className="mt-2 w-100"
              onChange={e => handleChange({ mappingId: e.target.value })}
              value={recipient.mappingId ?? ''}
              required
            />
          </div>
        </div>
        <div>
          <h6 className="text-info-dark">Partnership ID</h6>
          <div className="mt-2 w-100">
            <input
              type="text"
              className="mt-2 w-100"
              onChange={e => handleChange({ partnershipId: e.target.value })}
              value={recipient.partnershipId ?? ''}
              required
            />
          </div>
        </div>
        <div>
          <h6 className="text-info-dark">Customer Code</h6>
          <div className="mt-2 w-100">
            <input
              type="text"
              className="mt-2 w-100"
              onChange={e => handleChange({ customerCode: e.target.value })}
              value={recipient.customerCode ?? ''}
            />
          </div>
        </div>
        <div>
          <h6 className="text-info-dark">Location Code</h6>
          <div className="mt-2 w-100">
            <input
              type="text"
              className="mt-2 w-100"
              onChange={e => handleChange({ locationCode: e.target.value })}
              value={recipient.locationCode ?? ''}
            />
          </div>
        </div>
        <div>
          <h6 className="text-info-dark">File Extension</h6>
          <div className="mt-2 w-100">
            <input
              type="text"
              className="mt-2 w-100"
              onChange={e => handleChange({ fileExtension: e.target.value })}
              value={recipient.fileExtension ?? ''}
              required
            />
          </div>
        </div>
        <div>
          <h6 className="text-info-dark">Enabled</h6>
          <div className="mt-2">
            <input
              type="checkbox"
              className="mt-2"
              defaultChecked={recipient.enabled}
              onChange={e => handleChange({ enabled: e.target.checked })}
            />
          </div>
        </div>
        <div className="flex-fill d-flex align-items-end h-100">
          <button
            className="btn-back p-2"
            type="button"
            onClick={handleDelete}
            disabled={customer.ediRecipients?.length === 0}
          >
            <span className="material-icons icon-xlarge">delete</span>
          </button>
        </div>
      </div>
      <div className="border-bottom"></div>
    </>
  );
};

export const NewCustomer: FC = () => {
  const navigate = useNavigate();
  const { createCustomer, validateCode } = useCustomerAPI();
  const { selectedLocation } = useSelectedLocation();
  const { timeZoneIndicator } = useTimeZone();

  const [customer, setCustomer] = useState<Partial<Customer>>({});

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);

  const setRecipients = (recipient?: EdiCustomerLocation, index?: number) => {
    setCustomer(previous => {
      const recipients = Object.assign([], previous.ediRecipients);
      if (!recipient) {
        recipients.splice(index ?? recipients.length, 1);
      } else {
        recipients.splice(index ?? recipients.length, 1, recipient);
      }
      return { ...previous, ediRecipients: recipients };
    });
  };

  const handleReset: FormEventHandler<HTMLFormElement> = useCallback(() => {
    navigate('/settings/customers', { replace: true });
  }, [navigate]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      setIsSubmitting(true);

      if (!customer || !customer.name || !customer.code) {
        setIsSubmitting(false);
        return;
      }

      if ((customer.notes?.length ?? 0) > NOTES_MAX_LENGTH) {
        setIsSubmitting(false);
        return;
      }

      // validate & submit request
      const response = await createCustomer({
        name: customer.name,
        code: customer.code,
        email: customer.email,
        location: selectedLocation ?? undefined,
        activityReportHours: customer.activityReportHours ?? [],
        statusReportHours: customer.statusReportHours ?? [],
        inventoryReportHours: customer.inventoryReportHours ?? [],
        ediRecipients: customer.ediRecipients ?? [],
        interchangeRecipients: customer.interchangeRecipients ?? [],
        sendAutomatedReport: customer.sendAutomatedReport ?? false,
        notes: customer.notes
      });

      setIsSubmitting(false);
      if (!response) {
        navigate('/settings/customers', { replace: true });
        return;
      }

      navigate(`/settings/customers/${response.id}`, { replace: true });
    },
    [createCustomer, customer, navigate, selectedLocation]
  );

  const validateCodeInput = useCallback(
    async (code: string): Promise<ValidationResult> => {
      if (!code || !selectedLocation?.id) {
        return { status: ValidationStatus.DEFAULT };
      }

      const isValid = await validateCode(code, selectedLocation.id);
      if (isValid) {
        return { status: ValidationStatus.SUCCESS };
      }

      return { status: ValidationStatus.WARNING, error: 'Code not available.' };
    },
    [selectedLocation, validateCode]
  );

  return (
    <NewCustomerContext.Provider
      value={{
        customer,
        formRef,
        setCustomer,
        setRecipients,
        handleReset,
        handleSubmit
      }}
    >
      <form
        className="page"
        id="new-customer"
        onReset={handleReset}
        onSubmit={handleSubmit}
        ref={formRef}
        data-cy="new-customer-form"
      >
        <div className="page-header">
          <h1>NEW CUSTOMER</h1>
          <div className="d-flex g-3 flex-row-reverse">
            <Loader loading={isSubmitting}>
              <button className="btn-primary" type="submit">
                SUBMIT
              </button>
            </Loader>
            <button className="btn-outline" type="reset">
              CANCEL
            </button>
          </div>
        </div>
        <div className="page-content flex-fill">
          <div className="card-primary">
            <div className="card-header px-3">
              <h4 className="text-uppercase">Customer Information</h4>
            </div>
            <div className="card-body">
              <div className="grid-4 p-2">
                <div>
                  <h6 className="text-info-dark">Name</h6>
                  <input
                    type="text"
                    className="mt-2 w-100"
                    defaultValue={customer.name ?? ''}
                    onChange={e => setCustomer(c => c && { ...c, name: e.target.value })}
                    required
                    data-cy="new-customer-name-input"
                  />
                </div>
                <div>
                  <h6 className="text-info-dark">Code</h6>
                  <TextField
                    className="mt-2"
                    value={customer.code ?? ''}
                    onChange={code => setCustomer(c => c && { ...c, code })}
                    validate={validateCodeInput}
                    data-cy="new-customer-code-input"
                  />
                </div>
                <div>
                  <h6 className="text-info-dark">Location</h6>
                  <p className="mt-2 text-black">{selectedLocation?.name}</p>
                </div>
              </div>
              <div className="grid-2 p-2">
                <div>
                  <h6 className="text-info-dark">Notes</h6>
                  <textarea
                    className="mt-2 w-100"
                    rows={3}
                    maxLength={NOTES_MAX_LENGTH}
                    value={customer.notes ?? ''}
                    onChange={e => setCustomer(c => c && { ...c, notes: e.target.value })}
                    data-cy="new-customer-notes-input"
                  />
                  <small
                    className={(customer.notes?.length ?? 0) >= NOTES_MAX_LENGTH ? 'text-danger' : 'text-info-dark'}
                  >
                    {customer.notes?.length ?? 0}/{NOTES_MAX_LENGTH}
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div className="card-primary">
            <div className="card-header px-3">
              <h4 className="text-uppercase">email</h4>
            </div>
            <div className="card-body">
              <div className="p-2 d-flex align-items-center g-2">
                <h6 className="text-info-dark mb-0">Send Automated Reports</h6>
                <label className="switch mb-0">
                  <input
                    type="checkbox"
                    checked={customer.sendAutomatedReport === true}
                    onChange={e => setCustomer(c => c && { ...c, sendAutomatedReport: e.target.checked })}
                    data-cy="new-customer-send-automated-reports-input"
                  />
                  <span className="slider round" />
                </label>
              </div>
              <div className="p-2">
                <h6 className="text-info-dark">Email Addresses</h6>
                <EmailSelect
                  email={customer?.email}
                  setEmail={email => setCustomer(c => c && { ...c, email })}
                />
              </div>
              <div className="p-2">
                <h6 className="text-info-dark">Activity Report ({timeZoneIndicator})</h6>
                <HourSelect
                  id="activity-report-hours"
                  hours={customer.activityReportHours ?? []}
                  setHours={activityReportHours => setCustomer(c => c && { ...c, activityReportHours })}
                />
              </div>
              <div className="p-2">
                <h6 className="text-info-dark">Status Report ({timeZoneIndicator})</h6>
                <HourSelect
                  id="status-report-hours"
                  hours={customer.statusReportHours ?? []}
                  setHours={statusReportHours => setCustomer(c => c && { ...c, statusReportHours })}
                />
              </div>
              <div className="p-2">
                <h6 className="text-info-dark">Inventory Report ({timeZoneIndicator})</h6>
                <HourSelect
                  id="inventory-report-hours"
                  hours={customer.inventoryReportHours ?? []}
                  setHours={inventoryReportHours => setCustomer(c => c && { ...c, inventoryReportHours })}
                />
              </div>
              <div className="p-2">
                <h6 className="text-info-dark">Interchange Receipt Email Addresses</h6>
                <EmailSelect
                  email={customer.interchangeRecipients?.join(",")}
                  setEmail={value => setCustomer(c => c && { ...c, interchangeRecipients: parseEmailList(value) })}
                />
              </div>
            </div>
          </div>
          <div className="card-primary">
            <div className="card-header px-3">
              <h4 className="text-uppercase">EDI Recipient(s)</h4>
            </div>
          </div>
          <div className="card-body">
            <div className="p-2">
              {customer.ediRecipients?.map((recipient, index) => (
                <Recipient recipient={recipient} index={index} key={`recipient-${index}`} />
              ))}
              <div className="p-2">
                <button className="btn-outline" type="button" onClick={() => setRecipients({} as EdiCustomerLocation)}>
                  Add Contact
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </NewCustomerContext.Provider>
  );
};
